import "../Styles/TrustedBy.css";
import { useEffect, useRef, useState } from "react";
import { useLang } from "../i18n/useLang";

function TrustedBy() {
  const { t } = useLang();

  const [isDown, setIsDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const partnersRef = useRef(null);
  const velocity = useRef(0);
  const animationFrameId = useRef(null);
  const lastClientX = useRef(0);
  const lastDragTime = useRef(0);
  const isDownRef = useRef(false);

  const partners = t.home.trusted.logos;
  const partnerLinks = [
    "https://metahomelb.com/",
    "http://essencaclinic.com/",
    "https://technohomelb.com/",
    "https://estethica-eu.com/",
    "https://getcolebanon.com/",
  ];
  const infinitePartners = [...partners, ...partners];

  const stopInertia = () => {
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }
  };

  const animateInertia = () => {
    if (!partnersRef.current) return;

    velocity.current *= 0.95;

    if (Math.abs(velocity.current) < 0.5) {
      stopInertia();
      return;
    }

    partnersRef.current.scrollLeft += velocity.current;
    animationFrameId.current = requestAnimationFrame(animateInertia);
  };

  const beginDrag = (clientX, pageX) => {
    stopInertia();
    setIsDown(true);
    isDownRef.current = true;

    if (!partnersRef.current) return;

    setStartX(pageX - partnersRef.current.offsetLeft);
    setScrollLeft(partnersRef.current.scrollLeft);
    lastClientX.current = clientX;
    lastDragTime.current = performance.now();
  };

  const updateDrag = (clientX, pageX) => {
    if (!isDown || !partnersRef.current) return;

    const currentTime = performance.now();
    const deltaX = clientX - lastClientX.current;
    const deltaTime = currentTime - lastDragTime.current;

    velocity.current = (-deltaX / (deltaTime > 0 ? deltaTime : 1)) * 4;

    lastClientX.current = clientX;
    lastDragTime.current = currentTime;

    const x = pageX - partnersRef.current.offsetLeft;
    const walk = x - startX;
    partnersRef.current.scrollLeft = scrollLeft - walk;
  };

  const endDrag = () => {
    setIsDown(false);
    isDownRef.current = false;
    if (Math.abs(velocity.current) > 2) {
      animationFrameId.current = requestAnimationFrame(animateInertia);
    }
  };

  // Mouse handlers
  const onMouseDown = (e) => {
    e.preventDefault();
    beginDrag(e.clientX, e.pageX);
  };

  const onMouseMove = (e) => {
    e.preventDefault();
    updateDrag(e.clientX, e.pageX);
  };

  const onMouseUp = () => endDrag();

  const onMouseLeave = () => {
    if (isDown) endDrag();
  };

  // Touch handlers (mobile)
  const onTouchStart = (e) => {
    const touch = e.touches[0];
    if (!touch) return;
    beginDrag(touch.clientX, touch.pageX);
  };

  const onTouchMove = (e) => {
    const touch = e.touches[0];
    if (!touch) return;
    updateDrag(touch.clientX, touch.pageX);
  };

  const onTouchEnd = () => endDrag();

  useEffect(() => {
    const container = partnersRef.current;
    if (!container) return;

    const handleScroll = () => {
      const loopPoint = container.scrollWidth / 2;
      const maxScrollLeft = container.scrollWidth - container.clientWidth;

      if (maxScrollLeft <= 0) return;

      if (loopPoint <= maxScrollLeft && container.scrollLeft >= loopPoint) {
        container.scrollLeft -= loopPoint;
      } else if (container.scrollLeft >= maxScrollLeft - 1) {
        container.scrollLeft = 0;
      }
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", handleScroll);
      stopInertia();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="trustedBy" aria-label="Trusted by section">
      <h2 className="trustedBy__title">{t.home.trusted.title}</h2>

      <div
        className={`trustedBy__partners ${isDown ? "is-dragging" : ""}`}
        ref={partnersRef}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onMouseMove={onMouseMove}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        role="region"
        aria-label={t.home.trusted.aria}
      >
        {infinitePartners.map((src, index) => {
          const partnerIndex = index % partners.length;

          return (
            <a
              key={`${src}-${index}`}
              href={partnerLinks[partnerIndex]}
              className="trustedBy__link"
              target="_blank"
              rel="noreferrer"
              aria-label={t.home.trusted.logoAlt.replace("{n}", String(partnerIndex + 1))}
            >
              <img
                src={src}
                alt={t.home.trusted.logoAlt.replace("{n}", String(partnerIndex + 1))}
                width={partnerIndex === 0 ? 160 : 360}
                height={partnerIndex === 0 ? 80 : 180}
                className={
                  partnerIndex === 0
                    ? "trustedBy__img trustedBy__img--small"
                    : "trustedBy__img"
                }
                loading="lazy"
                decoding="async"
              />
            </a>
          );
        })}
      </div>
    </div>
  );
}

export default TrustedBy;
